/* eslint-disable @typescript-eslint/no-explicit-any */
import Container from "../components/Container";
import Text from "../components/Text";
import { useQuery } from "@tanstack/react-query";
import useAuthHttp from "../hooks/useAuthHttp";
import Currency from "../components/Currency";
import { Skeleton } from "primereact/skeleton";
import { Chart } from "primereact/chart";
import { Button } from "primereact/button";
import { useState } from "react";
import { months } from "../utils/date";
import moment from "moment";
import Card from "../components/Card";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import AssetAllocationCardItem from "../components/cards/AssetAllocationCardItem";
import Show from "../components/Show";

interface Event {
    event_date: string;
    event_value: number;
    asset_id: string;
    asset_name: string;
    event_type: string;
    event_fee: number;
    payment_date: string;
    income_tax: number | null;
    factor: number | null;
    old_value: number | null;
    new_value: number | null;
}

interface MonthEvents {
    paid_events: Event[];
    pending_events: Event[] | null;
}

interface EventEvolutionMonth {
    month_reference: string;
    month_events: MonthEvents;
    amount_month_paid: number;
    amount_month_pending: number;
}

interface EventsData {
    user_id: string;
    event_evolution_month: EventEvolutionMonth[];
    amount_event_value_yr: number;
    amount_event_value_12_month: number;
    average_event_value: number;
}

interface IEventSummaryProps {
    amount_event_value_yr: number;
    amount_event_value_12_month: number;
    average_event_value: number;
}

const EventSummary = ({
    amount_event_value_12_month,
    amount_event_value_yr,
    average_event_value
}: IEventSummaryProps) => {
    return (
        <Container wrap gap={3} fullWidth>
            <Container className="flex flex-1 bg-white border-1 border-gray-300 gap-2 p-3 border-round justify-center items-center" flexColumn>
                <Text className="text-sm text-900">Recebidos neste ano</Text>
                <Currency value={amount_event_value_yr}>
                    {(value) => (
                        <Text className="font-bold text-900 text-lg">{value}</Text>
                    )}
                </Currency>
            </Container>

            <Container className="flex flex-1 bg-white border-1 border-gray-300 gap-2 p-3 border-round justify-center items-center" flexColumn>
                <Text className="text-sm text-900">Recebidos nos últimos 12 meses</Text>
                <Currency value={amount_event_value_12_month}>
                    {(value) => (
                        <Text className="font-bold text-900 text-lg">{value}</Text>
                    )}
                </Currency>
            </Container>

            <Container className="flex flex-1 bg-white border-1 border-gray-300 gap-2 p-3 border-round justify-center items-center" flexColumn>
                <Text className="text-sm text-900">Média mensal histórica</Text>
                <Currency value={average_event_value}>
                    {(value) => (
                        <Text className="font-bold text-900 text-lg">{value}</Text>
                    )}
                </Currency>
            </Container>
        </Container>
    );
}

interface IScheduleProps {
    event_evolution_month: EventEvolutionMonth[];
}

const Schedule = ({ event_evolution_month }: IScheduleProps) => {
    const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth());
    const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());

    return (
        <Container fullWidth gap={2} flexColumn>
            <Container fullWidth>
                <Card
                    title="Agenda"
                    subTitle="Agenda da distribuição de proventos e dividendos"
                    action={
                        <Container center gap={2}>
                            <Button
                                icon="pi pi-arrow-left"
                                outlined
                                rounded
                                className="border-none"
                                onClick={() => {
                                    setSelectedYear(selectedYear => selectedYear - 1);
                                }}
                            />

                            <Text color="primary" className="text-2xl font-bold">
                                {selectedYear}
                            </Text>

                            <Button
                                icon="pi pi-arrow-right"
                                outlined
                                rounded
                                className="border-none"
                                onClick={() => {
                                    setSelectedYear(selectedYear => selectedYear + 1);
                                }}
                            />
                        </Container>
                    }
                ><></></Card>
            </Container>

            <Container wrap gap={3} center fullWidth>
                {
                    months.map((month, index) => {
                        const monthEvent = event_evolution_month
                            .find(e =>
                                moment(e.month_reference, "YYYY-MM")
                                    .isSame(
                                        moment(`${selectedYear}-${(index + 1).toString().padStart(2, "0")}`, "YYYY-MM"),
                                        "month"
                                    )
                            );

                        const eventsCount = (monthEvent?.month_events.paid_events?.length ?? 0) + (monthEvent?.month_events.pending_events?.length ?? 0);

                        return (
                            <Button
                                className={`hover:bg-primary-100 border-1 border-round border-gray-300 px-5 py-2 flex flex-column gap-3 justify-content-center align-items-center ${selectedMonth == index ? "bg-primary-500" : "bg-white"}`}
                                onClick={() => {
                                    setSelectedMonth(index);
                                }}
                                key={`${selectedYear}-${(index + 1).toString().padStart(2, "0")}`}
                            >
                                <Text color={(selectedMonth == index) ? "0" : "900"}>{month.slice(0, 3)}</Text>
                                <Text
                                    color="0"
                                    className={`${selectedMonth == index ? "bg-green-500" : eventsCount > 0 ? "bg-gray-700" : "bg-gray-400"} w-2rem h-2rem flex justify-content-center align-items-center border-circle`}
                                >
                                    {eventsCount}
                                </Text>
                            </Button>
                        )
                    })
                }
            </Container>

            <Container className="table-responsive w-full">
                <DataTable
                    value={
                        [
                            ...event_evolution_month.find(e =>
                                moment(e.month_reference, "YYYY-MM")
                                    .isSame(
                                        moment(`${selectedYear}-${(selectedMonth + 1).toString().padStart(2, "0")}`, "YYYY-MM"),
                                        "month")
                            )?.month_events.paid_events ?? [],
                            ...event_evolution_month.find(e =>
                                moment(e.month_reference, "YYYY-MM")
                                    .isSame(
                                        moment(`${selectedYear}-${(selectedMonth + 1).toString().padStart(2, "0")}`, "YYYY-MM"),
                                        "month")
                            )?.month_events.pending_events ?? []
                        ]
                    }
                    className="w-full"
                    emptyMessage="Nenhum evento encontrado para o mês selecionado"
                >
                    <Column field="asset_name" header="Ativo" />
                    <Column field="event_type" header="Tipo" />
                    <Column
                        field="event_date"
                        header="Data do Evento"
                        body={(rowData: Event) => {
                            return (
                                moment(rowData.event_date).format("DD/MM/YYYY")
                            );
                        }}
                    />
                    <Column
                        field="payment_date"
                        header="Data de Pagamento"
                        body={(rowData: Event) => {
                            return (
                                moment(rowData.payment_date).format("DD/MM/YYYY")
                            );
                        }}
                    />
                    <Column
                        field="event_value"
                        header="Valor"
                        body={(rowData: Event) => {
                            return (
                                <Currency value={rowData.event_value}>
                                    {(value) => (
                                        <span>{value}</span>
                                    )}
                                </Currency>
                            );
                        }}
                    />
                    <Column
                        field="event_fee"
                        header="Valor por cota"
                        body={(rowData: Event) => {
                            return (
                                <Currency value={rowData.event_fee} maximumFractionDigits={4}>
                                    {(value) => (
                                        <span>{value}</span>
                                    )}
                                </Currency>
                            )
                        }}
                    />
                </DataTable>
            </Container>
        </Container>
    )
}

const ChartEvents = ({ event_evolution_month }: IScheduleProps) => {
    const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());

    const documentStyle = getComputedStyle(document.documentElement);
    const textColor = documentStyle.getPropertyValue('--text-color');
    const textColorSecondary = documentStyle.getPropertyValue('--text-color-secondary');
    const surfaceBorder = documentStyle.getPropertyValue('--surface-border');

    const eventEvolutionYear = Array.from({length: 12}).map((_, i) => {
        const month = i + 1;
        const year = selectedYear;

        const monthData = event_evolution_month.find(event => moment(event.month_reference, "YYYY-MM").isSame(`${year}-${month.toString().padStart(2, "0")}`, "month"));

        if (monthData) {
            return monthData;
        }

        return {
            month_reference: `${year}-${month.toString().padStart(2, "0")}`,
            month_events: {
                paid_events: [],
                pending_events: []
            },
            amount_month_paid: 0,
            amount_month_pending: 0
        };
    });

    const chartData = {
        labels: [...eventEvolutionYear.map(events => `${months[moment(events.month_reference, "YYYY-MM").toDate().getMonth()]} ${moment(events.month_reference, "YYYY-MM").toDate().getFullYear()}`) ?? []],
        datasets: [
            {
                type: 'bar',
                label: 'Total Recebido',
                backgroundColor: documentStyle.getPropertyValue('--primary-500'),
                data: [...eventEvolutionYear.map(events => events.amount_month_paid) ?? []]
            },
            {
                type: 'bar',
                label: 'Total a Receber',
                backgroundColor: documentStyle.getPropertyValue('--secondary-color'),
                data: [...eventEvolutionYear.map(events => events.amount_month_pending) ?? []]
            }
        ]
    };
    const chartOptions = {
        maintainAspectRatio: false,
        aspectRatio: 0.8,
        animation: {
            duration: 0
        },
        plugins: {
            tooltips: {
                mode: 'index',
                intersect: false
            },
            legend: {
                labels: {
                    color: textColor
                }
            },
            tooltip: {
                callbacks: {
                    label: function (context: any) {
                        const value = context.raw || 0;
                        const formattedValue = new Intl.NumberFormat("pt-BR", {
                            style: "currency",
                            currency: "BRL",
                        }).format(value);

                        return ` ${formattedValue}`;
                    },
                },
            }
        },
        scales: {
            x: {
                stacked: true,
                ticks: {
                    color: textColorSecondary
                },
                grid: {
                    color: surfaceBorder
                }
            },
            y: {
                stacked: true,
                ticks: {
                    color: textColorSecondary,
                    callback: function (value: any) {
                        return new Intl.NumberFormat("pt-BR", {
                            style: "currency",
                            currency: "BRL",
                        }).format(value);
                    }
                },
                grid: {
                    color: surfaceBorder
                }
            }
        }
    };

    return (
        <Container fullWidth>
            <Card>
                <Container className="justify-content-between mb-4 w-full">
                    <Container gap={3}>
                        <Container flexColumn>
                            <Text size="sm" fontWeight="semibold">TOTAL RECEBIDO NO ANO</Text>
                            <Currency value={eventEvolutionYear.reduce((acc, event) => acc + event.amount_month_paid, 0)}>
                                {(value) => (
                                    <Text color="primary" className="font-bold text-lg">{value}</Text>
                                )}
                            </Currency>
                        </Container>

                        <Container flexColumn>
                            <Text size="sm" fontWeight="semibold">TOTAL A RECEBER NO ANO</Text>
                            <Currency value={eventEvolutionYear.reduce((acc, event) => acc + event.amount_month_pending, 0)}>
                                {(value) => (
                                    <Text className="font-bold text-lg" color="secondary">{value}</Text>
                                )}
                            </Currency>
                        </Container>
                    </Container>

                    <Container center gap={2}>
                        <Button
                            icon="pi pi-arrow-left"
                            outlined
                            rounded
                            className="border-none"
                            onClick={() => {
                                setSelectedYear(selectedYear => selectedYear - 1);
                            }}
                        />

                        <Text color="primary" className="text-2xl font-bold">
                            {selectedYear}
                        </Text>

                        <Button
                            icon="pi pi-arrow-right"
                            outlined
                            rounded
                            className="border-none"
                            disabled={selectedYear >= new Date().getFullYear()}
                            onClick={() => {
                                setSelectedYear(old => {
                                    if (old >= new Date().getFullYear()) {
                                        return old;
                                    }
                                    return selectedYear + 1;
                                });
                            }}
                        />
                    </Container>
                </Container>

                <Container flexColumn fullWidth>
                    <Chart
                        type="bar"
                        data={chartData}
                        options={chartOptions}
                    />
                </Container>
            </Card>
        </Container>
    );
}

const DividendDistribution = ({ event_evolution_month }: IScheduleProps) => {
    const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
    const groupedEvents = event_evolution_month
        .filter(eventMonth => moment(eventMonth.month_reference, "YYYY-MM").year() === selectedYear)
        .reduce((acc, eventMonth) => {
            eventMonth.month_events.paid_events?.forEach(event => {
                if (!acc[event.asset_name]) {
                    acc[event.asset_name] = {
                        name: event.asset_name,
                        value: 0,
                        percentage: 0,
                        color: "bg-primary-500"
                    };
                }
                acc[event.asset_name].value += event.event_value;
            });

            eventMonth.month_events.pending_events?.forEach(event => {
                if (!acc[event.asset_name]) {
                    acc[event.asset_name] = {
                        name: event.asset_name,
                        value: 0,
                        percentage: 0,
                        color: "bg-secondary"
                    };
                }
                acc[event.asset_name].value += event.event_value;
            });
            return acc;
        }, {} as Record<string, { name: string; value: number; percentage: number; color: string }>);

    const totalValue = Object.values(groupedEvents ?? {}).reduce((acc, event) => acc + event.value, 0);

    Object.values(groupedEvents ?? {}).forEach(event => {
        event.percentage = (event.value / totalValue) * 100;
    });

    return (
        <Container fullWidth>
            <Card
                title="Distribuição de proventos"
                subTitle="Distribuição de proventos por agrupamento"
                action={
                    <Container center gap={2}>
                        <Button
                            icon="pi pi-arrow-left"
                            outlined
                            rounded
                            className="border-none"
                            onClick={() => {
                                setSelectedYear(selectedYear => selectedYear - 1);
                            }}
                        />

                        <Text color="primary" className="text-2xl font-bold">
                            {selectedYear}
                        </Text>

                        <Button
                            icon="pi pi-arrow-right"
                            outlined
                            rounded
                            className="border-none"
                            onClick={() => {
                                setSelectedYear(selectedYear => selectedYear + 1);
                            }}
                        />
                    </Container>
                }
            >
                <Show when={!Object.values(groupedEvents).length}>
                    <Container center>
                        <Text color="900" fontWeight="normal">Nenhum evento encontrado para o ano selecionado</Text>
                    </Container>
                </Show>

                <Container wrap className="w-full" gap={3}>
                    {
                        Object.values(groupedEvents ?? {})
                            .sort((a, b) => b.percentage - a.percentage)
                            .map(event => (
                                <AssetAllocationCardItem
                                    key={event.name}
                                    name={event.name}
                                    percentage={event.percentage}
                                    value={event.value}
                                    color={event.color}
                                />
                            ))
                    }
                </Container>
            </Card>
        </Container>
    );
};

export default function EventsPage() {
    const authHttp = useAuthHttp();
    const eventsQuery = useQuery({
        queryKey: ["events"],
        queryFn: async () => {
            const response = await authHttp.doGet<EventsData>(`events`);
            const allMonths = Array.from({ length: 12 }, (_, i) => (i + 1).toString().padStart(2, "0"));
            const allYears = Array.from(new Set(response.event_evolution_month.map(eventMonth => moment(eventMonth.month_reference, "YYYY-MM").year())));
            const filledEventEvolutionMonth = allYears.flatMap(year => {
                return allMonths.map(month => {
                    const monthReference = `${year}-${month}`;
                    const existingMonth = response.event_evolution_month.find(eventMonth => eventMonth.month_reference === monthReference);
                    return existingMonth || {
                        month_reference: monthReference,
                        month_events: {
                            paid_events: [],
                            pending_events: null
                        },
                        amount_month_paid: 0,
                        amount_month_pending: 0
                    };
                });
            });

            const years = [new Date().getFullYear(), new Date().getFullYear() + 1];
            const currentYearMonths = years.flatMap(currentYear => allMonths.map(month => {
                const monthReference = `${currentYear}-${month}`;
                const existingMonth = response.event_evolution_month.find(eventMonth => eventMonth.month_reference === monthReference);
                return existingMonth || {
                    month_reference: monthReference,
                    month_events: {
                        paid_events: [],
                        pending_events: null
                    },
                    amount_month_paid: 0,
                    amount_month_pending: 0
                };
            }));

            filledEventEvolutionMonth.push(...currentYearMonths.filter(month => !filledEventEvolutionMonth.some(existingMonth => existingMonth.month_reference === month.month_reference)));
            response.event_evolution_month = filledEventEvolutionMonth;
            return {
                ...response,
                event_evolution_month: response.event_evolution_month.sort((a, b) => moment(a.month_reference, "YYYY-MM").toDate().getTime() - moment(b.month_reference, "YYYY-MM").toDate().getTime())
            };
        },
    });

    return (
        <Container flexColumn center fullWidth className="flex justify-end items-center" gap={3}>
            {
                eventsQuery.isPending && (
                    <>
                        <Container wrap gap={3} fullWidth>
                            <Skeleton height="90px" className="flex border-1 bg-gray-200 border-gray-300 flex-1" />
                            <Skeleton height="90px" className="flex border-1 bg-gray-200 border-gray-300 flex-1" />
                            <Skeleton height="90px" className="flex border-1 bg-gray-200 border-gray-300 flex-1" />
                        </Container>

                        <Container wrap gap={3} fullWidth>
                            <Skeleton height="300px" className="flex border-1 bg-gray-200 border-gray-300 flex-1" />
                        </Container>
                    </>
                )
            }

            {
                !eventsQuery.isPending && (
                    <>
                        <EventSummary
                            amount_event_value_yr={eventsQuery.data?.amount_event_value_yr ?? 0}
                            amount_event_value_12_month={eventsQuery.data?.amount_event_value_12_month ?? 0}
                            average_event_value={eventsQuery.data?.average_event_value ?? 0}
                        />

                        <ChartEvents
                            event_evolution_month={eventsQuery.data?.event_evolution_month ?? []}
                        />

                        <Schedule
                            event_evolution_month={eventsQuery.data?.event_evolution_month ?? []}
                        />
                    </>
                )
            }

            {
                !eventsQuery.isPending && (
                    <DividendDistribution
                        event_evolution_month={eventsQuery.data?.event_evolution_month ?? []}
                    />
                )
            }
        </Container>
    )
}